import { AthletePreview, WorkoutLayerDomain } from '@strength/domain';
import {
  HasWorkoutLayer,
  WorkoutAlternateGroup,
  WorkoutExercise,
  WorkoutSuperset,
} from '@strength/domain-workout';
import { atom } from 'jotai';
import {
  WorkoutLayerCommand,
  WorkoutLayerModelName,
} from '../../mutation-fns/types';
import {
  findWorkoutLayer,
  editExerciseEdits,
  createWorkoutLayerEdit,
} from '../../mutation-fns/workout-draft';
import {
  AthleteDraftWorkoutLayer,
  BlockDraftWorkoutLayer,
  WorkoutWithCycleAndSession,
} from '../../types';
import { ApplySwapData } from './ApplySwapDrawer';

export type TrackExerciseSwapInfo = {
  workoutId: string;
  exerciseId: string;
  oldExerciseName: string;
  newExerciseName: string;
  oldLibraryExerciseId: string;
  newLibraryExerciseId: string;
};

export type ApplySwapCallbacks = {
  onOpenApplySwap: (data: ApplySwapData) => void;
  onTrackExerciseSwap: (info: TrackExerciseSwapInfo) => void;
};

/**
 * Tracks exercises that were recently swapped in the current session.
 * Keyed by `${workoutId}:${newLibraryExerciseId}` so we can look up
 * whether a rendered exercise was just swapped.
 *
 * This atom is ephemeral — entries should be cleared when the user
 * changes views or leaves the page.
 */
export const recentSwapsAtom = atom<Map<string, ApplySwapData>>(new Map());

/**
 * Find a recent swap entry for a rendered exercise by scanning the map.
 * The map is ephemeral and tiny (cleared on view change), so iteration is fine.
 */
export function findRecentSwap(
  swaps: Map<string, ApplySwapData>,
  workoutId: string,
  libraryExerciseId: string,
): ApplySwapData | undefined {
  for (const data of swaps.values()) {
    if (
      data.workoutId === workoutId &&
      data.newLibraryExerciseId === libraryExerciseId
    ) {
      return data;
    }
  }
  return undefined;
}

/** Return a new map with the swap entry added, keyed by exerciseId. */
export function addRecentSwap(
  prev: Map<string, ApplySwapData>,
  data: ApplySwapData,
): Map<string, ApplySwapData> {
  const next = new Map(prev);
  next.set(data.exerciseId, data);
  return next;
}

/** Return a new map with the swap entry removed by exerciseId. */
export function removeRecentSwap(
  prev: Map<string, ApplySwapData>,
  exerciseId: string,
): Map<string, ApplySwapData> {
  const next = new Map(prev);
  next.delete(exerciseId);
  return next;
}

export type MatchingExerciseInfo = {
  workoutId: string;
  workoutExerciseId: string;
  workoutSectionId: string;
  libraryExerciseId: string;
};

/**
 * Collect all exercises from a section entry, including those nested inside
 * supersets and alternate groups.
 */
function collectExercisesFromEntry(
  entry: WorkoutExercise | WorkoutSuperset | WorkoutAlternateGroup,
): Pick<WorkoutExercise, 'workoutExerciseId' | 'libraryExerciseId'>[] {
  if (entry.__type === 'WorkoutExercise') {
    return [entry];
  }
  if (entry.__type === 'WorkoutSuperset') {
    return entry.exercises.map((e) => e.exercise);
  }
  if (entry.__type === 'WorkoutAlternateGroup') {
    return entry.exercises.map((e) => e.exercise);
  }
  return [];
}

/**
 * Given a current workoutId and an old libraryExerciseId, find all exercises in
 * workouts that come AFTER the current one (by cycle then session order) that
 * share the same libraryExerciseId. Only include planned (and in-progress) workouts.
 * Traverses into supersets and alternate groups so nested exercises are included.
 */
export function findMatchingExercisesInFutureWorkouts(
  currentWorkoutId: string,
  oldLibraryExerciseId: string,
  workouts: WorkoutWithCycleAndSession[],
): MatchingExerciseInfo[] {
  const current = workouts.find((w) => w.workoutId === currentWorkoutId);
  const currentCycle = current?.cycle.id ?? 0;
  const currentSession = current?.session.id ?? 0;

  const results: MatchingExerciseInfo[] = [];

  for (const workout of workouts) {
    if (workout.workoutId === currentWorkoutId) continue;
    if (['completed', 'skipped', 'canceled'].includes(workout.status.type)) {
      continue;
    }

    // Only include workouts strictly after the current one in program order
    const isAfter =
      workout.cycle.id > currentCycle ||
      (workout.cycle.id === currentCycle &&
        workout.session.id > currentSession);
    if (!isAfter) continue;

    for (const section of workout.sections) {
      for (const entry of section.entries) {
        for (const exercise of collectExercisesFromEntry(entry)) {
          if (exercise.libraryExerciseId === oldLibraryExerciseId) {
            results.push({
              workoutId: workout.workoutId,
              workoutExerciseId: exercise.workoutExerciseId,
              workoutSectionId: section.workoutSectionId,
              libraryExerciseId: exercise.libraryExerciseId,
            });
          }
        }
      }
    }
  }

  return results;
}

/**
 * Generate WorkoutLayerCommands to swap each matching exercise to a new library exercise.
 * This edits the libraryExerciseId and exerciseName on each matching exercise entry
 * across the provided workout layers.
 */
export function generateSwapCommandsForFutureInstances(
  matchingExercises: MatchingExerciseInfo[],
  newLibraryExerciseId: string,
  newExerciseName: string,
  modelName: WorkoutLayerModelName,
  workoutLayers: HasWorkoutLayer[],
  baseLayers?: HasWorkoutLayer[],
): WorkoutLayerCommand[] {
  // Group by workoutId so we produce one merged command per workout.
  // Without this, two matches in the same workout would each snapshot the
  // same base layer and the second command would overwrite the first.
  const matchesByWorkoutId = new Map<string, MatchingExerciseInfo[]>();
  for (const match of matchingExercises) {
    const group = matchesByWorkoutId.get(match.workoutId);
    if (group) {
      group.push(match);
    } else {
      matchesByWorkoutId.set(match.workoutId, [match]);
    }
  }

  return [...matchesByWorkoutId.entries()].map(([workoutId, matches]) => {
    const { identity, workoutLayer } = findWorkoutLayer(
      workoutLayers,
      workoutId,
      modelName,
      baseLayers,
    );

    const edits = matches.flatMap((match) =>
      editExerciseEdits(workoutLayer, match.workoutExerciseId, {
        libraryExerciseId: newLibraryExerciseId,
        exerciseName: newExerciseName,
      }),
    );

    return createWorkoutLayerEdit(identity, workoutLayer, edits);
  });
}

/**
 * Find exercises matching oldLibraryExerciseId for a specific athlete,
 * considering both block-layer and athlete-layer data.
 *
 * An exercise matches if:
 * 1. The block layer has an exerciseAdd with oldLibraryExerciseId AND the
 *    athlete hasn't overridden it to a different libraryExerciseId, OR
 * 2. The athlete layer has an exerciseEdit that sets libraryExerciseId TO
 *    oldLibraryExerciseId (e.g. from a previous bulk swap), OR
 * 3. The athlete layer has an exerciseAdd with oldLibraryExerciseId
 */
export function findMatchingExercisesForAthlete(
  blockWorkoutLayers: BlockDraftWorkoutLayer[],
  athleteLayers: AthleteDraftWorkoutLayer[],
  oldLibraryExerciseId: string,
): MatchingExerciseInfo[] {
  const results: MatchingExerciseInfo[] = [];
  const seen = new Set<string>(); // avoid duplicates by workoutExerciseId

  // Build a map of athlete exerciseEdits keyed by workoutExerciseId for fast lookup
  const athleteEditsByExerciseId = new Map<
    string,
    { libraryExerciseId?: string | null; workoutId: string }
  >();
  for (const layer of athleteLayers) {
    const wl = layer.layer as WorkoutLayerDomain.WorkoutLayer | null;
    if (!wl) continue;
    for (const edit of wl.exerciseEdits) {
      athleteEditsByExerciseId.set(edit.workoutExerciseId, {
        libraryExerciseId: edit.libraryExerciseId,
        workoutId: layer.workoutId,
      });
    }
  }

  // Case 1: block layer exerciseAdds not overridden away
  for (const layer of blockWorkoutLayers) {
    const wl = layer.layer as WorkoutLayerDomain.WorkoutLayer | null;
    if (!wl) continue;

    for (const add of wl.exerciseAdds) {
      if (add.libraryExerciseId !== oldLibraryExerciseId) continue;

      const override = athleteEditsByExerciseId.get(add.workoutExerciseId);
      if (
        override &&
        override.libraryExerciseId !== undefined &&
        override.libraryExerciseId !== null &&
        override.libraryExerciseId !== oldLibraryExerciseId
      ) {
        // Athlete overrode this exercise to something else — skip
        continue;
      }

      if (!seen.has(add.workoutExerciseId)) {
        seen.add(add.workoutExerciseId);
        results.push({
          workoutId: layer.workoutId,
          workoutExerciseId: add.workoutExerciseId,
          workoutSectionId: add.workoutSectionId,
          libraryExerciseId: add.libraryExerciseId,
        });
      }
    }
  }

  // Case 2: athlete layer exerciseEdits pointing TO oldLibraryExerciseId
  for (const layer of athleteLayers) {
    const wl = layer.layer as WorkoutLayerDomain.WorkoutLayer | null;
    if (!wl) continue;

    for (const edit of wl.exerciseEdits) {
      if (edit.libraryExerciseId !== oldLibraryExerciseId) continue;
      if (seen.has(edit.workoutExerciseId)) continue;

      seen.add(edit.workoutExerciseId);
      results.push({
        workoutId: layer.workoutId,
        workoutExerciseId: edit.workoutExerciseId,
        workoutSectionId: '', // not available on edits; unused by generateSwapCommands
        libraryExerciseId: oldLibraryExerciseId,
      });
    }
  }

  // Case 3: athlete layer exerciseAdds with oldLibraryExerciseId
  for (const layer of athleteLayers) {
    const wl = layer.layer as WorkoutLayerDomain.WorkoutLayer | null;
    if (!wl) continue;

    for (const add of wl.exerciseAdds) {
      if (add.libraryExerciseId !== oldLibraryExerciseId) continue;
      if (seen.has(add.workoutExerciseId)) continue;

      seen.add(add.workoutExerciseId);
      results.push({
        workoutId: layer.workoutId,
        workoutExerciseId: add.workoutExerciseId,
        workoutSectionId: add.workoutSectionId,
        libraryExerciseId: add.libraryExerciseId,
      });
    }
  }

  return results;
}

/**
 * For each athlete (excluding the current profile), determine whether they have
 * a matching exercise that could be swapped.
 *
 * An athlete has the old exercise if:
 * 1. The block layer has an exerciseAdd with oldLibraryExerciseId AND the
 *    athlete hasn't overridden it to a different libraryExerciseId, OR
 * 2. The athlete layer has an exerciseEdit that sets libraryExerciseId TO
 *    oldLibraryExerciseId (a previous bulk swap applied it), OR
 * 3. The athlete layer has an exerciseAdd with oldLibraryExerciseId
 *    (athlete-only exercise)
 */
export function findMatchingAthletesForSwap(
  athletes: AthletePreview[],
  blockWorkoutLayers: BlockDraftWorkoutLayer[],
  allAthleteDraftWorkoutLayers: AthleteDraftWorkoutLayer[],
  currentProfileId: string,
  oldLibraryExerciseId: string,
): { athlete: AthletePreview; hasMatch: boolean }[] {
  // Collect the workoutExerciseIds from the block layer that have the old exercise
  const blockExerciseIds = new Set<string>();
  for (const layer of blockWorkoutLayers) {
    const workoutLayer = layer.layer as WorkoutLayerDomain.WorkoutLayer | null;
    if (!workoutLayer) continue;
    for (const add of workoutLayer.exerciseAdds) {
      if (add.libraryExerciseId === oldLibraryExerciseId) {
        blockExerciseIds.add(add.workoutExerciseId);
      }
    }
  }

  return athletes
    .filter((athlete) => athlete.id !== currentProfileId)
    .map((athlete) => {
      const athleteLayers = allAthleteDraftWorkoutLayers.filter(
        (l) => l.profileId === athlete.id,
      );

      // --- Case 1: block layer has the exercise, athlete hasn't overridden it ---
      // Collect block-layer exercise IDs that the athlete has overridden away
      const overriddenBlockExerciseIds = new Set<string>();
      for (const layer of athleteLayers) {
        const workoutLayer =
          layer.layer as WorkoutLayerDomain.WorkoutLayer | null;
        if (!workoutLayer) continue;

        for (const edit of workoutLayer.exerciseEdits) {
          if (!blockExerciseIds.has(edit.workoutExerciseId)) continue;
          if (
            edit.libraryExerciseId !== undefined &&
            edit.libraryExerciseId !== null &&
            edit.libraryExerciseId !== oldLibraryExerciseId
          ) {
            overriddenBlockExerciseIds.add(edit.workoutExerciseId);
          }
        }
      }

      const hasUnoverriddenBlockExercise = [...blockExerciseIds].some(
        (id) => !overriddenBlockExerciseIds.has(id),
      );

      if (hasUnoverriddenBlockExercise) {
        return { athlete, hasMatch: true };
      }

      // --- Case 2: athlete layer has an exerciseEdit pointing TO the old ID ---
      for (const layer of athleteLayers) {
        const workoutLayer =
          layer.layer as WorkoutLayerDomain.WorkoutLayer | null;
        if (!workoutLayer) continue;

        const hasEditToOld = workoutLayer.exerciseEdits.some(
          (edit) => edit.libraryExerciseId === oldLibraryExerciseId,
        );
        if (hasEditToOld) {
          return { athlete, hasMatch: true };
        }
      }

      // --- Case 3: athlete layer has an exerciseAdd with the old ID ---
      for (const layer of athleteLayers) {
        const workoutLayer =
          layer.layer as WorkoutLayerDomain.WorkoutLayer | null;
        if (!workoutLayer) continue;

        const hasAddWithOld = workoutLayer.exerciseAdds.some(
          (add) => add.libraryExerciseId === oldLibraryExerciseId,
        );
        if (hasAddWithOld) {
          return { athlete, hasMatch: true };
        }
      }

      return { athlete, hasMatch: false };
    });
}
